import { getCustomerProfile } from '@/features/auth/authService';
import { fetchProfileBookings } from '@/features/profile/profileService';
import { createAsyncThunk } from '@/store/utils';
import type { CustomerProfileDetailsResponseDTO } from '@/types/dto/agent';
import type { BookingSearchResponse, BookingsState } from '@/types/profile';
import { getTokensRef } from '@/utils/auth';
import { createAction } from '@reduxjs/toolkit';

export const clearProfile = createAction('profile/clearProfile');

export const getProfile =
  createAsyncThunk<CustomerProfileDetailsResponseDTO | null>(
    'profile/getProfile',
    async () => {
      const tokensRef = getTokensRef();

      if (!tokensRef) {
        return null;
      }

      try {
        return await getCustomerProfile();
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        return null;
      }
    }
  );

export const getProfileBookings = createAsyncThunk<BookingsState | null, void>(
  'profile/getProfileBookings',
  async (_, { getState }) => {
    const { current } = getState().profile;

    const [
      activeTickets,
      historyTickets,
      activeTravelPasses,
      historyTravelPasses,
    ] = await Promise.all([
      fetchProfileBookings({
        customerProfileCode: current?.accountCode,
        customerProfileBookingsActivityType: 'ACTIVE',
        productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
      }),
      fetchProfileBookings({
        customerProfileCode: current?.accountCode,
        customerProfileBookingsActivityType: 'INACTIVE',
        productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
      }),
      fetchProfileBookings({
        customerProfileCode: current?.accountCode,
        customerProfileBookingsActivityType: 'ACTIVE',
        productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
      }),
      fetchProfileBookings({
        customerProfileCode: current?.accountCode,
        customerProfileBookingsActivityType: 'INACTIVE',
        productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
      }),
    ]);

    return {
      tickets: { active: activeTickets, history: historyTickets },
      travelPasses: {
        active: activeTravelPasses,
        history: historyTravelPasses,
      },
    };
  }
);

export const getProfileActiveTickets = createAsyncThunk<
  BookingSearchResponse,
  void
>('profile/getProfileActiveTickets', async (_, { getState }) => {
  const { current, bookings } = getState().profile;
  const offset = bookings?.tickets.active.data.length ?? 0;

  return fetchProfileBookings(
    {
      customerProfileCode: current?.accountCode,
      customerProfileBookingsActivityType: 'ACTIVE',
      productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
    },
    offset
  );
});

export const getProfileHistoryTickets = createAsyncThunk<
  BookingSearchResponse,
  void
>('profile/getProfileHistoryTickets', async (_, { getState }) => {
  const { current, bookings } = getState().profile;
  const offset = bookings?.tickets.history.data.length ?? 0;

  return fetchProfileBookings(
    {
      customerProfileCode: current?.accountCode,
      customerProfileBookingsActivityType: 'INACTIVE',
      productCategories: ['PRODUCT_CATEGORY.ADMISSION'],
    },
    offset
  );
});

export const getProfileActiveTravelPasses = createAsyncThunk<
  BookingSearchResponse,
  void
>('profile/getProfileActiveTravelPasses', async (_, { getState }) => {
  const { current, bookings } = getState().profile;
  const offset = bookings?.travelPasses.active.data.length ?? 0;

  return fetchProfileBookings(
    {
      customerProfileCode: current?.accountCode,
      customerProfileBookingsActivityType: 'ACTIVE',
      productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
    },
    offset
  );
});

export const getProfileHistoryTravelPasses = createAsyncThunk<
  BookingSearchResponse,
  void
>('profile/getProfileHistoryTravelPasses', async (_, { getState }) => {
  const { current, bookings } = getState().profile;
  const offset = bookings?.travelPasses.history.data.length ?? 0;

  return fetchProfileBookings(
    {
      customerProfileCode: current?.accountCode,
      customerProfileBookingsActivityType: 'INACTIVE',
      productCategories: ['PRODUCT_CATEGORY.TRAVEL_PASS'],
    },
    offset
  );
});
